'use client'

import { Box, Flex, HStack, Skeleton, Spacer, Stack } from '@chakra-ui/react'
import { Page } from '@saas-ui/react'

import { CHAT_WIDTH, SessionHeader } from './session-header'

/**
 * Line widths (as a % of the chat column) for each placeholder message. Fixed
 * rather than random so the skeleton doesn't reshuffle on re-render, and varied
 * so the block reads as prose instead of a stack of identical bars.
 */
const PLACEHOLDER_MESSAGES: ReadonlyArray<ReadonlyArray<string>> = [
  ['92%', '78%', '55%'],
  ['85%', '48%'],
  ['96%', '88%', '70%', '40%'],
]

/**
 * Loading state for the session page.
 *
 * It reuses the page's own chrome — the same header grid, the same 480px chat
 * column, the same composer and preview-control bars — so the frame is already
 * in place when the session data arrives and only the content inside it fills
 * in. The previous "Loading…" text left the page blank and then snapped into a
 * two-column layout.
 */
export function SessionPageSkeleton() {
  return (
    <Page.Root>
      <SessionHeader
        title={<Skeleton height="4" width="44" borderRadius="l1" />}
        actions={
          <HStack gap="3" w="full" minW="0">
            {/* View-mode segmented control */}
            <Skeleton height="6" width="56" borderRadius="l2" />
            <Spacer />
            {/* Participants */}
            <HStack gap="1">
              <Skeleton boxSize="6" borderRadius="full" />
              <Skeleton boxSize="6" borderRadius="full" />
            </HStack>
            {/* Repo badge + branch / PR actions */}
            <Skeleton height="6" width="28" borderRadius="l2" />
            <Skeleton height="6" width="20" borderRadius="l2" />
          </HStack>
        }
      />
      <Page.Body p="0" flex="1" minH="0">
        <Flex
          direction="row"
          h="full"
          minH="0"
          overflow="hidden"
          role="status"
          aria-label="Loading session"
        >
          {/* Left: conversation */}
          <Flex
            direction="column"
            w={CHAT_WIDTH}
            flexShrink="0"
            minH="0"
            overflow="hidden"
          >
            <Stack flex="1" minH="0" gap="7" px="4" py="8" overflow="hidden">
              {PLACEHOLDER_MESSAGES.map((lines, messageIndex) => (
                <HStack key={messageIndex} align="flex-start" gap="3">
                  <Skeleton boxSize="8" borderRadius="full" flexShrink="0" />
                  <Stack gap="2" flex="1" minW="0" pt="1">
                    <Skeleton height="3" width="24" borderRadius="l1" />
                    {lines.map((width, lineIndex) => (
                      <Skeleton
                        key={lineIndex}
                        height="3"
                        width={width}
                        borderRadius="l1"
                      />
                    ))}
                  </Stack>
                </HStack>
              ))}
            </Stack>

            {/* Composer */}
            <Box borderTopWidth="1px" borderColor="border" p="3">
              <Stack gap="2">
                <Skeleton height="16" borderRadius="l2" />
                <HStack gap="3">
                  <Skeleton height="6" width="32" borderRadius="l2" />
                  <Spacer />
                  <Skeleton height="8" width="16" borderRadius="l2" />
                </HStack>
              </Stack>
            </Box>
          </Flex>

          {/* Right: preview pane */}
          <Flex
            direction="column"
            flex="1"
            minW="0"
            minH="0"
            borderLeftWidth="1px"
            borderColor="border"
          >
            <HStack
              gap="2"
              h="10"
              px="3"
              flexShrink="0"
              borderBottomWidth="1px"
              borderColor="border"
            >
              <Box flex="1" minW="0" />
              {/* Address bar */}
              <Skeleton
                height="6"
                w="full"
                maxW="360px"
                flexShrink="1"
                borderRadius="full"
              />
              <HStack flex="1" minW="0" justify="flex-end" gap="1">
                <Skeleton boxSize="6" borderRadius="l2" />
                <Skeleton boxSize="6" borderRadius="l2" />
              </HStack>
            </HStack>
            <Box flex="1" minH="0" p="3">
              <Skeleton h="full" borderRadius="l2" />
            </Box>
          </Flex>
        </Flex>
      </Page.Body>
    </Page.Root>
  )
}
